import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { TypedHTTP } from '@flakiness/shared/common/typedHttp.js';
import { ContextType } from '@lit/context';
import { Task, TaskStatus } from '@lit/task';
import { SlDialog } from '@shoelace-style/shoelace';
import { live } from 'lit/directives/live.js';
import { api } from './api.js';
import { docLinkStyles, pageStyles } from './components/cssstyles.js';
import { ProjectContext } from './components/project-context.js';
import { UserSelectedEvent } from './components/search-users.js';
import { consume, contexts } from './contexts.js';
import { RELINKABLE_KINDS, SourceCodeIntegration, SourceCodeIntegrationEvent } from './features/feature-source-code-integration.js';
import { forgeUtils } from './forgeUtils.js';
import { renderRelativeDate } from './fragments.js';
import { githubMarkdownCSS } from './githubMarkdownCSS.js';
import { projectPlatformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { PageProjectSettings } from './page-project-settings.js';
import { tasks } from './tasks.js';

@customElement('page-project-access')
export class PageProjectAccess extends LitElement {

  static url(options: { orgSlug: string, projectSlug: string, snapshotId: WireTypes.DataSnapshotId | undefined }): string {
    return projectPlatformUrl(['', options.orgSlug, options.projectSlug, 'access'].join('/'), undefined, options.snapshotId);
  }

  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/:project/access',
        // The page used to be called "Collaborators".
        '/:org/:project/collaborators',
      ],
      render: (groups) => ProjectContext.render({
        orgSlug: groups?.org,
        projectSlug: groups?.project,
      }, html`
        <page-project-access
          .orgSlug=${groups?.org}
          .projectSlug=${groups?.project}
        ></page-project-access>
      `),
    }];
  }

  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;
  @consume({ context: contexts.serverInfo, subscribe: true }) private _serverInfo?: ContextType<typeof contexts.serverInfo>;

  @property({ attribute: false }) projectSlug?: string;
  @property({ attribute: false }) orgSlug?: string;

  @query('#add-people-dialog') private _addPeopleDialog?: SlDialog;
  @query('#add-repository-dialog') private _addRepositoryDialog?: SlDialog;
  @query('#remove-repository-dialog') private _removeRepositoryDialog?: SlDialog;
  @state() private _grantToRemove?: WireTypes.OIDCGrant;
  @state() private _integration?: SourceCodeIntegration;
  @state() private _addRepositoryError?: string;

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug, this.projectSlug] as const,
    metadata: ([orgSlug, projectSlug]) => orgSlug && projectSlug ? pageMetadata({
      orgSlug, projectSlug, section: 'Access',
    }) : undefined,
  });

  private _collaboratorsTask = new Task(this, {
    args: () => [this.orgSlug, this.projectSlug] as const,
    task: async ([orgSlug, projectSlug], { signal }) => {
      if (!orgSlug || !projectSlug)
        return undefined;
      return await api.project.users.GET({ orgSlug, projectSlug }, { signal });
    },
  });

  // Only editors can see the repositories that are granted access.
  private _oidcGrantsTask = new Task(this, {
    args: () => [this.orgSlug, this.projectSlug, this._project?.access] as const,
    task: async ([orgSlug, projectSlug, access], { signal }) => {
      if (!orgSlug || !projectSlug || access !== 'editor')
        return undefined;
      return await api.project.oidcGrants.GET({ orgSlug, projectSlug }, { signal });
    },
  });

  private _addRepositoryTask = new Task(this, {
    autoRun: false,
    args: () => [this.orgSlug, this.projectSlug, this._integration] as const,
    task: async ([orgSlug, projectSlug, integration], { signal }) => {
      if (!orgSlug || !projectSlug || !integration)
        return;
      if (integration.kind !== 'github_app' && integration.kind !== 'github_pat' && integration.kind !== 'gitlab_pat')
        return;
      this._addRepositoryError = undefined;
      const repository = integration.kind === 'github_app' ? { type: 'githubApp' as const, owner: integration.owner, repo: integration.repo } :
        integration.kind === 'github_pat' ? { type: 'githubPat' as const, owner: integration.owner, repo: integration.repo, accessToken: integration.accessToken } :
        { type: 'gitlabPat' as const, owner: integration.owner, repo: integration.repo, accessToken: integration.accessToken };
      try {
        await api.project.addOIDCGrant.POST({ orgSlug, projectSlug, repository }, { signal });
      } catch (e) {
        this._addRepositoryError = e instanceof TypedHTTP.HttpError ? e.message : 'Something went wrong; please try again later.';
        return;
      }
      this._addRepositoryDialog?.hide();
      this._oidcGrantsTask.run();
    },
  });

  private _removeGrantTask = new Task(this, {
    autoRun: false,
    args: () => [this.orgSlug, this.projectSlug, this._grantToRemove] as const,
    task: async ([orgSlug, projectSlug, grant], { signal }) => {
      if (!orgSlug || !projectSlug || !grant)
        return;
      await api.project.removeOIDCGrant.POST({ orgSlug, projectSlug, grantId: grant.grantId }, { signal });
      this._removeRepositoryDialog?.hide();
      this._oidcGrantsTask.run();
    },
  });

  private _confirmRemoveGrant(grant: WireTypes.OIDCGrant) {
    this._grantToRemove = grant;
    this._removeRepositoryDialog?.show();
  }

  private _showAddRepositoryDialog() {
    this._addRepositoryError = undefined;
    this._addRepositoryDialog?.show();
  }

  render() {
    if (!this._project)
      return nothing;

    return html`
      <sl-dialog id=add-people-dialog style="--width: 60ch;" label="Add people">
        <search-users
          placeholder="Search people"
          @user-selected=${(event: UserSelectedEvent) => this._addCollaborator(event.user)}
        ></search-users>
      </sl-dialog>

      <sl-dialog id=add-repository-dialog style="--width: 75ch;" label="Add repository"
        @sl-after-hide=${(event: Event) => {
          // Shoelace re-dispatches the event from nested components.
          if (event.target === this._addRepositoryDialog)
            this._integration = undefined;
        }}
      >
        <p class=secondary>
          CI workflows in the repository will be able to read and upload reports
          using OIDC.
        </p>
        <feature-source-code-integration
          .allowedKinds=${RELINKABLE_KINDS}
          @source-code-integration=${(event: SourceCodeIntegrationEvent) => {
            this._integration = event.detail;
            this._addRepositoryError = undefined;
          }}
        ></feature-source-code-integration>
        ${this._addRepositoryError ? html`
          <fk-callout variant=danger>${this._addRepositoryError}</fk-callout>
        ` : nothing}
        <sl-button
          slot=footer
          variant=primary
          ?disabled=${!this._integration}
          ?loading=${this._addRepositoryTask.status === TaskStatus.PENDING}
          @click=${() => this._addRepositoryTask.run()}
        >Add repository</sl-button>
      </sl-dialog>

      <sl-dialog id=remove-repository-dialog label="Remove repository">
        ${this._grantToRemove ? html`
          <p>
            Remove <strong>${this._grantToRemove.ownerName}/${this._grantToRemove.repoName}</strong>?
            Its workflows will lose access to this project.
          </p>
        ` : nothing}
        <sl-button slot=footer @click=${() => this._removeRepositoryDialog?.hide()}>Cancel</sl-button>
        <sl-button
          slot=footer
          variant=danger
          ?loading=${this._removeGrantTask.status === TaskStatus.PENDING}
          @click=${() => this._removeGrantTask.run()}
        >Remove repository</sl-button>
      </sl-dialog>

      <project-header
        .orgSlug=${this.orgSlug}
        .projectSlug=${this.projectSlug}
        .submenu=${"access"}
      ></project-header>
      <app-body user-role=${this._project.access}>
        <section class=access-section>
          <header>
            <div>
              <h2>People</h2>
              <div class=secondary>Collaborators with access to this project, in addition to organization members.</div>
            </div>
            <sl-button is-editor size=small @click=${() => this._addPeopleDialog?.show()}>
              <sl-icon slot=prefix name=plus-lg></sl-icon>
              Add people
            </sl-button>
          </header>
          <div class=list @collaborator-removed=${() => this._collaboratorsTask.run()}>
            ${this._collaboratorsTask.render({
              pending: () => html`<div class="row empty"><sl-spinner></sl-spinner></div>`,
              error: () => html`<div class="row empty">Failed to load collaborators; please try again later.</div>`,
              complete: collaborators => !collaborators?.length ?
                html`<div class="row empty">No collaborators yet</div>` :
                collaborators.map(({ user, userRole }) => html`
                  <project-collaborator class=row
                    .project=${this._project}
                    .user=${user}
                    .projectRole=${live(userRole)}
                  ></project-collaborator>
                `),
            })}
          </div>
        </section>

        <section class=access-section is-editor>
          <header>
            <div>
              <h2>CI Repositories</h2>
              <div class=secondary>Workflows in these repositories can read and upload reports using OIDC.</div>
            </div>
            <sl-button size=small @click=${() => this._showAddRepositoryDialog()}>
              <sl-icon slot=prefix name=plus-lg></sl-icon>
              Add repository
            </sl-button>
          </header>
          <div class=list>
            ${this._renderLinkedRepository(this._project)}
            ${this._oidcGrantsTask.render({
              pending: () => html`<div class="row empty"><sl-spinner></sl-spinner></div>`,
              error: () => html`<div class="row empty">Failed to load repositories; please try again later.</div>`,
              complete: grants => !grants?.length && !forgeUtils.hasSourceUrl(this._project) ?
                html`<div class="row empty">No repositories yet</div>` :
                grants?.map(grant => this._renderGrant(grant)),
            })}
          </div>
        </section>
      </app-body>
      <app-footer></app-footer>
    `
  }

  private _renderLinkedRepository(project: WireTypes.Project) {
    if (!forgeUtils.hasSourceUrl(project))
      return nothing;
    return html`
      <div class="row repository">
        <div class=provider><sl-icon name=${forgeUtils.providerIcon(project)}></sl-icon></div>
        <div class=lines>
          <a-ext href=${forgeUtils.projectUrl(project)}>${project.sourceOwner}/<strong>${project.sourceRepo}</strong></a-ext>
          <div class=secondary>Always has access · change it in <a href=${PageProjectSettings.url({ orgSlug: project.org.orgSlug, projectSlug: project.projectSlug, snapshotId: this._snapshotId })}>Settings</a></div>
        </div>
        <span class=secondary>Linked</span>
      </div>
    `;
  }

  private _renderGrant(grant: WireTypes.OIDCGrant) {
    const host = grant.provider === 'gitlab' ? (this._serverInfo?.gitlabOrigin ?? 'https://gitlab.com') : 'https://github.com';
    return html`
      <div class="row repository">
        <div class=provider><sl-icon name=${grant.provider}></sl-icon></div>
        <div class=lines>
          <a-ext href=${`${host}/${grant.ownerName}/${grant.repoName}`}>${grant.ownerName}/<strong>${grant.repoName}</strong></a-ext>
          <div class=secondary>${grant.lastUsedTimestampSeconds !== undefined ? html`
            Last used ${renderRelativeDate(grant.lastUsedTimestampSeconds * 1000)}
          ` : html`Never used`}</div>
        </div>
        <sl-button size=small variant=danger outline @click=${() => this._confirmRemoveGrant(grant)}>Remove</sl-button>
      </div>
    `;
  }

  private async _addCollaborator(user: WireTypes.User) {
    const { orgSlug, projectSlug } = this;
    if (!orgSlug || !projectSlug)
      return;
    await api.project.shareProject.POST({
      orgSlug,
      projectSlug,
      userId: user.userId,
      access: 'viewer',
    });
    this._addPeopleDialog?.hide();
    this._collaboratorsTask.run();
  }

  static styles = [githubMarkdownCSS, docLinkStyles, pageStyles, css`
    [user-role=viewer] {
      *:is([is-editor]) {
        display: none;
      }
    }

    .secondary {
      color: var(--sl-color-neutral-500);
      font-size: var(--sl-font-size-small);
    }

    .access-section {
      margin-bottom: var(--sl-spacing-2x-large);

      header {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: var(--sl-spacing-medium);
        margin-bottom: var(--sl-spacing-small);
      }

      h2 {
        font-size: var(--sl-font-size-medium);
        font-weight: var(--sl-font-weight-bold);
        margin: 0 0 var(--sl-spacing-3x-small) 0;
        padding: 0;
        border: none;
      }
    }

    .list {
      border: 1px solid var(--fk-color-border);
      border-radius: var(--sl-border-radius-large);

      .row {
        min-height: 2rem;
        padding: var(--sl-spacing-small) var(--sl-spacing-medium);

        & + .row {
          border-top: 1px solid var(--fk-color-border);
        }
      }

      .empty {
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--sl-color-neutral-500);
      }
    }

    .repository {
      display: grid;
      grid-template-columns: auto 1fr auto;
      align-items: center;
      gap: var(--sl-spacing-small);

      .provider {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
        border-radius: var(--sl-border-radius-medium);
        background-color: var(--fk-color-canvas);
        border: 1px solid var(--fk-color-border);
        box-sizing: border-box;
      }
    }

    feature-source-code-integration {
      margin-top: var(--sl-spacing-medium);
    }
  `];
}
